
import ApiService from '../api'

class SurveyService {
  // ============ ADMIN SIDE METHODS ============

  async getSurveys() {
    try {
      const response = await ApiService.get('/surveys')

      if (response && response.Data && response.Data.success && response.Data.data) {
        return {
          success: true,
          data: response.Data.data
        }
      } else if (response && response.success && response.data) {
        return response
      } else if (response && Array.isArray(response)) {
        return {
          success: true,
          data: response
        }
      } else {
        return response
      }
    } catch (error) {
      console.error('Get surveys error:', error)
      throw error
    }
  }

  async createSurvey(surveyData) {
    try {
      const response = await ApiService.post('/surveys', {}, surveyData)
      return response
    } catch (error) {
      console.error('Create survey error:', error)
      throw error
    }
  }

  async getSurvey(surveyId) {
    try {
      const response = await ApiService.get(`/surveys/${surveyId}`)
      return response
    } catch (error) {
      console.error('Get survey error:', error)
      throw error
    }
  }

  async updateSurvey(surveyId, surveyData) {
    try {
      const response = await ApiService.put(`/surveys/${surveyId}/edit`, {}, surveyData)
      return response
    } catch (error) {
      console.error('Update survey error:', error)
      throw error
    }
  }

  async previewSurvey(surveyId) {
    try {
      const response = await ApiService.get(`/surveys/${surveyId}/preview`)
      return response
    } catch (error) {
      console.error('Preview survey error:', error)
      throw error
    }
  }

  async getManagementData(surveyId) {
    try {
      const response = await ApiService.get(`/surveys/${surveyId}/manage`)
      return response
    } catch (error) {
      console.error('Error getting management data:', error)
      throw error
    }
  }

  async sendSurveyInvitations(surveyId, userIds) {
    try {
      const response = await ApiService.post(`/surveys/${surveyId}/send`, {}, { user_ids: userIds })
      return response
    } catch (error) {
      console.error('Send survey invitations error:', error)
      throw error
    }
  }

  async manageInvitations(surveyId, data) {
    try {
      const response = await ApiService.post(`/surveys/${surveyId}/manage-invitations`, {}, data)
      return response
    } catch (error) {
      console.error('Error managing invitations:', error)
      throw error
    }
  }

  async getSurveyResults(surveyId) {
    try {
      const response = await ApiService.get(`/surveys/${surveyId}/results`)

      if (response && response.Data) {
        const responseData = {
          success: response.Data.success,
          data: response.Data.data,
          message: response.Data.message || response.Message
        }

        if (responseData.success && responseData.data) {
          const formattedData = this.formatSurveyResultsData(responseData.data)
          return {
            success: true,
            data: formattedData,
            message: responseData.message
          }
        }
        return responseData
      } else if (response && response.success !== undefined) {
        if (response.success && response.data) {
          const formattedData = this.formatSurveyResultsData(response.data)
          return {
            success: true,
            data: formattedData,
            message: response.message
          }
        }
        return response
      } else {
        return {
          success: false,
          data: null,
          message: 'Unexpected response format'
        }
      }
    } catch (error) {
      console.error('Get survey results error:', error)
      return {
        success: false,
        data: null,
        message: error.message || 'Error loading survey results'
      }
    }
  }

  formatSurveyResultsData(rawData) {

    const formattedData = {
      survey: rawData.survey || {},
      questions: [],
      responses: [],
      statistics: rawData.statistics || {}
    }

    if (rawData.survey && rawData.survey.structure && rawData.survey.structure.questions) {
      formattedData.questions = rawData.survey.structure.questions.map(question => ({
        id: question.id,
        text: question.text,
        type: question.type,
        options: question.options || [],
        required: question.required || false,
        order: question.order || 1
      }))
    } else if (rawData.questions) {
      formattedData.questions = rawData.questions
    }

    if (rawData.responses && Array.isArray(rawData.responses)) {
      formattedData.responses = rawData.responses.map(response => {

        let parsedAnswers = {}

        if (response.answers) {
          if (typeof response.answers === 'string') {
            try {
              parsedAnswers = JSON.parse(response.answers)
            } catch (e) {
              console.error('Failed to parse answers JSON for response:', response.id, e)
            }
          } else if (typeof response.answers === 'object') {
            parsedAnswers = response.answers
          }
        }

        if (response.survey_answers) {
          if (typeof response.survey_answers === 'string') {
            try {
              const surveyAnswers = JSON.parse(response.survey_answers)
              parsedAnswers = { ...parsedAnswers, ...surveyAnswers }
            } catch (e) {
              console.error('Failed to parse survey_answers JSON for response:', response.id, e)
            }
          } else if (typeof response.survey_answers === 'object') {
            parsedAnswers = { ...parsedAnswers, ...response.survey_answers }
          }
        }

        return {
          id: response.id,
          user: {
            id: response.user?.id || response.user_id,
            name: response.user?.name || response.user_name || 'Unknown User',
            email: response.user?.email || response.user_email || 'No email'
          },
          submitted_at: response.submitted_at || response.created_at,
          answers: parsedAnswers,
          answer_elements: response.answer_elements || []
        }
      })
    } else if (rawData.survey_responses) {
      formattedData.responses = rawData.survey_responses.map(response => {
        let parsedAnswers = {}
        if (response.answers) {
          if (typeof response.answers === 'string') {
            try {
              parsedAnswers = JSON.parse(response.answers)
            } catch (e) {
              console.error('Failed to parse answers JSON for survey response:', response.id, e)
            }
          } else if (typeof response.answers === 'object') {
            parsedAnswers = response.answers
          }
        }
        return {
          id: response.id,
          user: {
            id: response.user?.id || response.user_id,
            name: response.user?.name || response.user_name || 'Unknown User',
            email: response.user?.email || response.user_email || 'No email'
          },
          submitted_at: response.submitted_at || response.created_at,
          answers: parsedAnswers,
          answer_elements: response.answer_elements || []
        }
      })
    }
    return formattedData
  }

  formatResponseAnswers(rawAnswers) {
    const formattedAnswers = {}
    if (typeof rawAnswers === 'string') {
      try {
        rawAnswers = JSON.parse(rawAnswers)
      } catch (e) {
        console.error('Failed to parse rawAnswers JSON:', e)
        return formattedAnswers
      }
    }
    if (typeof rawAnswers === 'object' && rawAnswers !== null) {
      Object.keys(rawAnswers).forEach(questionId => {
        const answer = rawAnswers[questionId]
        if (typeof answer === 'string') {
          formattedAnswers[questionId] = {
            answer_text: answer,
            answer_key: answer
          }
        } else if (answer && typeof answer === 'object') {
          formattedAnswers[questionId] = {
            answer_text: answer.answer_text || answer.answer_key || answer.value || '',
            answer_key: answer.answer_key || answer.answer_text || answer.value || ''
          }
        }
      })
    }
    return formattedAnswers
  }

  async getIndividualResponse(surveyId, userId) {
    try {
      const response = await ApiService.get(`/surveys/${surveyId}/responses/${userId}`)

      if (response && response.Data) {
        return {
          success: response.Data.success,
          data: response.Data.data,
          message: response.Data.message || response.Message
        }
      } else if (response && response.success !== undefined) {
        return response
      } else {
        return {
          success: false,
          data: null,
          message: 'Unexpected response format'
        }
      }
    } catch (error) {
      console.error('Get individual response error:', error)
      return {
        success: false,
        data: null,
        message: error.message || 'Error loading individual response'
      }
    }
  }

  async closeSurvey(surveyId) {
    try {
      const response = await ApiService.put(`/surveys/${surveyId}/close`, {}, {})
      return response
    } catch (error) {
      console.error('Close survey error:', error)
      throw error
    }
  }

  async deleteSurvey(surveyId) {
    try {
      const response = await ApiService.delete(`/surveys/${surveyId}`)
      return response
    } catch (error) {
      console.error('Delete survey error:', error)
      throw error
    }
  }

  async restoreSurvey(surveyId) {
    try {
      const response = await ApiService.put(`/surveys/${surveyId}/restore`, {}, {})
      return response
    } catch (error) {
      console.error('Restore survey error:', error)
      throw error
    }
  }

  // ============ USER SIDE METHODS ============

  async getMySurveys() {
    try {
      const response = await ApiService.get('/my-surveys')

      if (response && response.Data && response.Data.success !== undefined) {
        return {
          success: response.Data.success,
          data: response.Data.data || [],
          message: response.Data.message || response.Message
        }
      } else if (response && response.success !== undefined) {
        return {
          success: response.success,
          data: response.data || [],
          message: response.message
        }
      } else if (response && Array.isArray(response)) {
        return {
          success: true,
          data: response,
          message: 'Surveys loaded successfully'
        }
      } else if (response && typeof response === 'object') {
        return {
          success: true,
          data: response.data || response.surveys || [],
          message: response.message || 'Surveys loaded'
        }
      } else {
        console.error('Unexpected response format:', response)
        return {
          success: false,
          data: [],
          message: 'Unexpected response format from server'
        }
      }
    } catch (error) {
      console.error('Get my surveys error:', error)

      if (error.response) {
        console.error('Server error response:', error.response.data)
        return {
          success: false,
          data: [],
          message: error.response.data?.message || `Server error: ${error.response.status}`
        }
      } else if (error.request) {
        console.error('Network error:', error.request)
        return {
          success: false,
          data: [],
          message: 'Network error - please check your connection'
        }
      } else {
        return {
          success: false,
          data: [],
          message: error.message || 'An unexpected error occurred'
        }
      }
    }
  }

  async acceptAndOpenSurvey(surveyId) {
    try {
      const response = await ApiService.post(`/surveys/${surveyId}/accept`, {}, {})

      if (response && response.Data) {
        return {
          success: response.Data.success,
          data: response.Data.data,
          message: response.Data.message || response.Message
        }
      } else if (response && response.success !== undefined) {
        return response
      } else {
        return {
          success: false,
          data: null,
          message: 'Unexpected response format'
        }
      }
    } catch (error) {
      console.error('Accept and open survey error:', error)
      return {
        success: false,
        data: null,
        message: error.message || 'Error accepting survey'
      }
    }
  }

  async continueSurvey(surveyId) {
    try {
      const response = await ApiService.get(`/surveys/${surveyId}/continue`)

      if (response && response.Data) {
        return {
          success: response.Data.success,
          data: response.Data.data,
          message: response.Data.message || response.Message
        }
      } else if (response && response.success !== undefined) {
        return response
      } else {
        return {
          success: false,
          data: null,
          message: 'Unexpected response format'
        }
      }
    } catch (error) {
      console.error('Continue survey error:', error)
      return {
        success: false,
        data: null,
        message: error.message || 'Error loading survey'
      }
    }
  }

  async saveAnswers(surveyId, answers) {
    try {
      const response = await ApiService.post(`/surveys/${surveyId}/save`, {}, { answers })

      if (response && response.Data) {
        return {
          success: response.Data.success,
          data: response.Data.data,
          message: response.Data.message || response.Message
        }
      } else if (response && response.success !== undefined) {
        return response
      } else {
        return {
          success: false,
          data: null,
          message: 'Unexpected response format'
        }
      }
    } catch (error) {
      console.error('Save answers error:', error)
      return {
        success: false,
        data: null,
        message: error.message || 'Error saving answers'
      }
    }
  }

  async submitSurvey(surveyId, answers) {
    try {
      const response = await ApiService.post(`/surveys/${surveyId}/submit`, {}, { answers })

      if (response && response.Data) {
        return {
          success: response.Data.success,
          data: response.Data.data,
          message: response.Data.message || response.Message
        }
      } else if (response && response.success !== undefined) {
        return response
      } else {
        return {
          success: false,
          data: null,
          message: 'Unexpected response format'
        }
      }
    } catch (error) {
      console.error('Submit survey error:', error)
      return {
        success: false,
        data: null,
        message: error.message || 'Error submitting survey'
      }
    }
  }

  // ============ UTILITY METHODS ============
  /**
   * Format survey structure for frontend use
   * @param {Object} structure - Raw survey structure from API
   * @returns {Object} Formatted structure
   */
  formatSurveyStructure(structure) {

    if (!structure) {
      return { questions: [] }
    }

    let questions = []

    if (typeof structure === 'string') {
      try {
        const parsed = JSON.parse(structure)
        questions = parsed.questions || []
      } catch (e) {
        console.error('Error parsing structure JSON:', e)
        return { questions: [] }
      }
    } else if (structure && structure.questions) {
      questions = structure.questions
    } else if (Array.isArray(structure)) {
      questions = structure
    }

    // Ensure each question has the right format
    const formattedQuestions = questions.map(question => {
      // Handle both object and array formats
      const q = {
        id: question.id || question.ID,
        text: question.text || question.Text || question.question,
        type: question.type || question.Type || 'text',
        required: question.required || question.Required || false,
        options: question.options || question.Options || [],
        placeholder: question.placeholder || question.Placeholder || ''
      }

      // Ensure boolean required field
      if (typeof q.required === 'string') {
        q.required = q.required.toLowerCase() === 'true'
      }
      return q
    })

    return {
      questions: formattedQuestions
    }
  }

  /**
   * Map API question types to frontend question types
   * @param {string} apiType - Question type from API
   * @returns {string} Frontend question type
   */
  mapQuestionType(apiType) {
    const typeMapping = {
      'single_line_text': 'text',
      'multiple_line_text': 'textarea',
      'multiple_choice_single': 'radio',
      'multiple_choice_multiple': 'checkbox',
      'dropdown': 'select',
      'rating_scale': 'rating',
      'yes_no': 'radio',
      'date': 'date',
      'email': 'email',
      'number': 'number'
    }
    return typeMapping[apiType] || apiType
  }

  /**
   * Map frontend question types to API question types
   * @param {string} frontendType - Question type from frontend
   * @returns {string} API question type
   */
  mapQuestionTypeToApi(frontendType) {
    const typeMapping = {
      'text': 'single_line_text',
      'textarea': 'multiple_line_text',
      'radio': 'multiple_choice_single',
      'checkbox': 'multiple_choice_multiple',
      'select': 'dropdown',
      'rating': 'rating_scale'
    }
    return typeMapping[frontendType] || frontendType
  }

  /**
   * Format answers for API submission
   * @param {Object} answers - Answers from frontend
   * @returns {Object} Formatted answers for API
   */
  formatAnswersForApi(answers) {
    const formatted = {}

    Object.keys(answers).forEach(key => {
      const answer = answers[key]

      if (answer !== null && answer !== undefined && answer !== '') {
        formatted[key] = {
          answer_key: answer.toString(),
          answer_text: answer.toString()
        }
      }
    })

    return formatted
  }

  /**
   * Format answers from API for frontend
   * @param {Object} apiAnswers - Answers from API
   * @returns {Object} Formatted answers for frontend
   */
  formatAnswersFromApi(apiAnswers) {
    const formattedAnswers = {}
    Object.keys(apiAnswers).forEach(questionId => {
      const answer = apiAnswers[questionId]
      if (answer.answer_text && answer.answer_text.includes(',')) {
        // Multiple choice multiple
        formattedAnswers[questionId] = answer.answer_text.split(', ')
      } else {
        // Single answer
        formattedAnswers[questionId] = answer.answer_text
      }
    })
    return formattedAnswers
  }

  /**
   * Validate required fields before submission
   * @param {Object} structure - Survey structure
   * @param {Object} answers - User answers
   * @returns {Object} Validation result
   */
  validateRequiredFields(structure, answers) {

    const missingFields = []
    let valid = true

    // Extract questions from structure
    let questions = []
    if (structure && structure.questions) {
      questions = structure.questions
    } else if (structure && Array.isArray(structure)) {
      questions = structure
    }

    if (!questions || questions.length === 0) {
      console.warn('No questions found for validation')
      return { valid: true, missingFields: [] }
    }

    questions.forEach(question => {

      // Check if question is required
      let isRequired = false
      if (typeof question.required === 'boolean') {
        isRequired = question.required
      } else if (typeof question.required === 'string') {
        isRequired = question.required.toLowerCase() === 'true' || question.required.toLowerCase() === 'yes'
      }

      if (isRequired) {
        const questionId = question.id
        let hasValidAnswer = false

        // Check if answer exists and has content
        if (answers && answers[questionId] !== undefined && answers[questionId] !== null) {
          const answer = answers[questionId]

          if (typeof answer === 'string') {
            hasValidAnswer = answer.trim() !== ''
          } else if (typeof answer === 'number') {
            hasValidAnswer = true
          } else if (Array.isArray(answer)) {
            hasValidAnswer = answer.length > 0 && answer.some(item => item && item.toString().trim() !== '')
          } else if (typeof answer === 'object' && answer !== null) {
            // Handle object format
            const answerText = answer.answer_text || answer.answer_key || answer.value
            hasValidAnswer = answerText && answerText.toString().trim() !== ''
          }
        }

        // Also check string version of question ID
        if (!hasValidAnswer && answers && answers[questionId.toString()] !== undefined) {
          const answer = answers[questionId.toString()]

          if (typeof answer === 'string') {
            hasValidAnswer = answer.trim() !== ''
          } else if (typeof answer === 'number') {
            hasValidAnswer = true
          } else if (Array.isArray(answer)) {
            hasValidAnswer = answer.length > 0
          } else if (typeof answer === 'object' && answer !== null) {
            const answerText = answer.answer_text || answer.answer_key || answer.value
            hasValidAnswer = answerText && answerText.toString().trim() !== ''
          }
        }

        if (!hasValidAnswer) {
          missingFields.push({
            id: questionId,
            text: question.text
          })
          valid = false
        }
      }
    })

    const result = { valid, missingFields }

    return result
  }

  /**
   * Get status badge class for frontend
   * @param {string} status - Survey status
   * @returns {string} CSS class
   */
  getStatusBadgeClass(status) {
    const classes = {
      'Active': 'bg-success',
      'Draft': 'bg-secondary',
      'Closed': 'bg-info',
      'Deleted': 'bg-danger',
      'In Progress': 'bg-primary',
      'Unread': 'bg-secondary',
      'Completed': 'bg-success'
    }
    return classes[status] || 'bg-secondary'
  }
}

export default new SurveyService()
